import { AvatarThumbnail, Color, EntryStatus, Icon, Icons, PascalCaseToTitleCase, VStack } from '@starrez/starryui';
import { color } from '@starrez/starryui9';
import { HStack, Stack, Text } from '@starrez/starryui9';
import React from 'react';
import { IDirectoryNodes } from '../graphql/useMainDirectoryQuery';
import { HoverOverContainer } from '../../../../.starryUI/layouts/HoverOverContainer';
import { strings } from '../localization';
import styled from 'styled-components';
import { useEntryQuery } from '../graphql/useEntryQuery';
import { buildColourMarker, StatusMarkerDot } from '../../../../.starryUI/grids/cells/entryStatusColumn/EntryStatusColumn';
import { useEntryDirectoryEvents } from '../context/EntryDirectoryContext';
import { StyledLinkButton } from '../../../../.starryUI/grids/cells/LinkColumn/LinkColumn';
import { AccountFlag, IncidentHoldFlag, MedicalFlag, NotesFlag, PrivacyFlag, ShieldNegativeFlag, ShieldPositiveFlag } from '../../../../.starryUI/icons';

export const EntrySummaryView = (props: IDirectoryNodes) => {
    const { data } = useEntryQuery({ entryID: props.entryID });
    const { openEntry } = useEntryDirectoryEvents();

    return (
        <HoverOverContainer>
            <VStack>
                <HeaderContainer>
                    <HStack gap="small" templateColumns="auto 1fr">
                        <AvatarThumbnail
                            Size="Largest"
                            AltText={`${props.nameFirst},${props.nameLast} ${strings.Entry_ProfilePicture}`}
                            Src={props.largeImage.url}
                        />
                        <Stack gap="xxSmall">
                            <Text renderAs="h3" variant="xLarge.medium">
                                {props.nameLast}, {props.nameFirst}
                            </Text>
                            <HStack gap="xxSmall" templateColumns="auto 1fr">
                                <Text color={Color.Grey.G70} variant="small.regular">
                                    {strings.Entry_ResidentNr}
                                </Text>
                                <Text color={Color.Grey.G70} variant="small.regular">
                                    {props.entryID}
                                </Text>
                            </HStack>

                            <HStack gap="xSmall" templateColumns="auto 1fr">
                                <StatusMarkerDot {...buildColourMarker(props.entryStatus.value as EntryStatus)} />
                                <Text variant="normal.regular">{PascalCaseToTitleCase(props.entryStatus.value)}</Text>
                            </HStack>
                        </Stack>
                    </HStack>
                </HeaderContainer>
                <BodyContainer>
                    <Stack gap="xSmall">
                        <HStack gap="xSmall" templateColumns="auto 1fr">
                            <Icon Icon={Icons.Mail} />
                            <Text variant="normal.regular">
                                <a href={`mailto:${props.email}`}>{props.email}</a>
                            </Text>
                        </HStack>
                        <HStack gap="xSmall" templateColumns="auto 1fr">
                            <Icon Icon={Icons.Phone} />
                            <Text variant="normal.regular">
                                <a href={`tel:${data?.entry.phoneNumber}`}>{data?.entry.phoneNumber}</a>
                            </Text>
                        </HStack>
                    </Stack>
                </BodyContainer>
                <BodyContainer>
                    <Stack gap="xSmall">
                        <HStack gap="xSmall" templateColumns="120px 1fr">
                            <Text variant="normal.regular">{strings.Entry_Gender}</Text>
                            <Text variant="normal.regular">{props.genderEnum_Description}</Text>
                        </HStack>
                        <HStack gap="xSmall" templateColumns="120px 1fr">
                            <Text variant="normal.regular">{strings.Entry_RoomType}</Text>
                            <Text variant="normal.regular">
                                {props.roomSpace_Description} / {props.roomType_Description}
                            </Text>
                        </HStack>
                        <HStack gap="xSmall" templateColumns="120px 1fr">
                            <Text variant="normal.regular">{strings.Entry_Location}</Text>
                            <Text variant="normal.regular">{props.roomLocation_Description}</Text>
                        </HStack>
                        <HStack gap="xSmall" templateColumns="120px 1fr">
                            <Text variant="normal.regular">{strings.Entry_Dates}</Text>
                            <Text variant="normal.regular">
                                {data?.entry.activeBooking.checkInDate} - {data?.entry.activeBooking.checkOutDate}
                            </Text>
                        </HStack>
                        <StyledLinkButton onClick={() => openEntry(Number(props.entryID))}>
                            <Text variant="normal.regular">{strings.Entry_ViewMoreDetails}</Text>
                        </StyledLinkButton>
                    </Stack>
                </BodyContainer>
            </VStack>

            {props.displayFlag && (
                <FlagsContainer>
                    <Stack gap="xSmall">
                        {props.situationResponse_Description !== 'No Response' && (
                            <HStack gap="xSmall" templateColumns="auto 1fr">
                                {props.situationResponse_Description === 'Okay' ? (
                                    <ShieldPositiveFlag color={color.Green.G60} />
                                ) : (
                                    <ShieldNegativeFlag color={color.Red.R60} />
                                )}
                                <Text variant="normal.regular">{props.situationResponse_Description}</Text>
                            </HStack>
                        )}
                        {props.directoryFlagMedical && (
                            <HStack gap="xSmall" templateColumns="auto 1fr">
                                <MedicalFlag color={color.Red.R60} />
                                <Text variant="normal.regular">
                                    {strings.Entry_MedicalLabel} {data?.entry.entryDetail.medical}
                                </Text>
                            </HStack>
                        )}
                        {props.directoryFlagPrivacy && (
                            <HStack gap="xSmall" templateColumns="auto 1fr">
                                <PrivacyFlag color={color.Grey.G80} />
                                <Text variant="normal.regular">{strings.Entry_Private}</Text>
                            </HStack>
                        )}
                        {props.directoryFlagAccount && (
                            <HStack gap="xSmall" templateColumns="auto 1fr">
                                <AccountFlag color={color.Green.G80} />
                                <Text variant="normal.regular">{strings.Entry_AccountHold}</Text>
                            </HStack>
                        )}
                        {props.directoryFlagNotes && (
                            <HStack gap="xSmall" templateColumns="auto 1fr">
                                <NotesFlag color={color.Yellow.Y60} />
                                <Text variant="normal.regular">
                                    {strings.Entry_NotesLabel} {data?.entry.entryDetail.comments}
                                </Text>
                            </HStack>
                        )}
                        {data?.entry.entryDetail.incidentHold && (
                            <HStack gap="xSmall" templateColumns="auto 1fr">
                                <IncidentHoldFlag color={color.Pink.P80} />
                                <Text variant="normal.regular">
                                    {strings.Entry_IncidentsLabel} {data?.entry.entryDetail.incidentHoldComments}
                                </Text>
                            </HStack>
                        )}
                    </Stack>
                </FlagsContainer>
            )}
            <FooterContainer>
                <Stack>
                    <Text color={color.Grey.G60} renderAs="small" variant="xSmall.regular">
                        {data?.entry.lastModifiedInfo}
                    </Text>
                </Stack>
            </FooterContainer>
        </HoverOverContainer>
    );
};

const HeaderContainer = styled.div`
    padding: 15px;
    border-bottom: 1px solid ${color.Grey.G20};
`;

const BodyContainer = styled.div`
    padding: 15px 0;
    margin: 0 15px;
    border-bottom: 1px solid ${color.Grey.G20};
`;

const FlagsContainer = styled.div`
    padding: 15px;
    border-bottom: 1px solid ${color.Grey.G20};
`;

const FooterContainer = styled.div`
    padding: 10px 15px;
`;
